import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import BrokerNomination from '../../components/doorlypro/BrokerNomination';
import LicenseEvidenceCard from '../../components/doorlypro/LicenseEvidenceCard';
import { PartnerTraining, modulesForPartner, trainingProgress } from './DoorlyProOnboarding';
import FirmMatchPrompt from '../../components/doorlypro/FirmMatchPrompt';
import ReadinessChecklist from '../../components/doorlypro/ReadinessChecklist';
import { useIsMobile } from '../../utils/useIsMobile';
import PartnerSurveyModal from '../../components/PartnerSurveyModal';
import TrustpilotPrompt, { isHighRating } from '../../components/TrustpilotPrompt';
import { Icon } from '../../utils/icons';
import { OPEN_EVA_EVENT } from '../../components/DoorlyChat';
import { getStatusMeta, isInProgress } from '../../constants/partnerStatuses';
import { statusBadgeLabel } from '../../utils/partnerStatusBadge';   // P6 (a): the status badge

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Partner-facing status palette + copy lives in one place:
// frontend/src/constants/partnerStatuses.js (mirrors backend/utils/partnerStatus.js).

// Status pill with a definition + "what's next" tooltip on hover (desktop) and
// tap (mobile). Rendered via a portal so the card's `overflow:hidden` can't clip it.
// ⚠️ partnerType IS PASSED THROUGH so the "what's next" line is true for THIS
// partner. Under v2.1 a realtor performs home selection; the shared copy says
// the buyer does, and a realtor who believes it loses the fee under §4.2.
// ⚠️ StatusPill IS GONE WITH THE RECENT BUYERS CARD, its only caller. The
// buyers list page renders its own; a second pill component kept here would
// be a second idea of how a status looks.

// The payout check's keys, in the partner's words (utils/partnerPayoutReadiness ITEMS).
const PAYOUT_WORDS = {
  payee: 'your account linked to your firm',
  agreement: 'your signed Partner Agreement',
  countersigned: 'the counter-signature from your firm\u2019s broker of record',
  banking: 'your firm\u2019s banking details',
  w9: 'a W-9',
};
export function payoutLine(missing) {
  const words = (missing || []).map((k) => PAYOUT_WORDS[k]).filter(Boolean);
  if (!words.length) return 'Nothing here blocks payment \u2014 these finish your setup.';
  const list = words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `Before your first partner fee payout, we need: ${list}.`;
}

export default function DoorlyProDashboard() {
  const navigate = useNavigate();
  const { token, partner } = useDoorlyProAuth();
  const isMobile = useIsMobile();
  // 🔑 A COUNTER THE TASK BUTTON BUMPS. BrokerNomination opens and scrolls on
  // every change, so clicking "Invite broker" twice works twice — a boolean that
  // was already true would fire nothing the second time.
  const [brokerOpen, setBrokerOpen] = useState(0);
  const [referrals, setReferrals] = useState([]);
  // 🔑 The "All time" figure, from the count DoorlyDesk reads too (GET /me/file-counts, utils/partnerFileCounts).
  const [fileCounts, setFileCounts] = useState(null);
  // 🚨 THREE FETCHES, ONE PAINT. `loading` covered referrals and alerts only, so
  // the page announced itself ready and THEN grew a My Tasks card and, for a new
  // partner, a firm-match prompt above it — the "loading, then loading again"
  // flash. The blocks that decide this page's height have to land before it is
  // shown, or the reader watches it assemble.
  //
  // ⚠️ SETTLED, NOT SUCCESSFUL. Each flag flips on failure too; a dashboard that
  // waits forever on a dead endpoint is worse than one missing a card.
  const [loaded, setLoaded] = useState({ lists: false, tasks: false, firm: false, role: false });
  const loading = !(loaded.lists && loaded.tasks && loaded.firm && loaded.role);

  // 🔑 WHETHER THIS PERSON RUNS THE FIRM, read from the server rather than the
  // token — account_role is granted after registration, so a JWT minted at
  // sign-up carries a stale answer for the life of the session. It decides which
  // cards this dashboard shows, so it belongs in the same first paint as the
  // rest; a card that appears a beat later is the flash this page just lost.
  const [isAccountHolder, setIsAccountHolder] = useState(false);
  // 🔑 TRAINING OPENS HERE NOW. The task pointed at /doorlypro/onboarding, which
  // since the profile step was removed is nothing but the deck — so the page was
  // right and the journey was wrong: a partner already on their dashboard was
  // navigated out of it to a URL that reads as sign-up.
  const [trainingOpen, setTrainingOpen] = useState(false);
  // ── 🔑 ?training=1 OPENS THE DECK, ?return= COMES BACK (push C) ──────────
  // The continue screen's "Start training" lands here. Before, it landed on a
  // plain dashboard and the return path was dropped, so finishing training left
  // the partner here instead of on the Submit page she started from.
  const [searchParams] = useSearchParams();
  // ⚠️ AN ENUMERATED LIST, as DoorlyProAgreement does: `return` is editable,
  // and a prefix match is an open redirect waiting for a path nobody vetted.
  const returnTo = (() => {
    const RETURN_ALLOWED = ['/doorlypro/submissions'];
    const r = searchParams.get('return') || '';
    return RETURN_ALLOWED.includes(r) ? r : null;
  })();
  useEffect(() => {
    if (searchParams.get('training') === '1') setTrainingOpen(true);
  }, [searchParams]);

  // ── 🚨 THE PAGE BEHIND A MODAL MUST NOT SCROLL ──────────────────────────
  //
  // The training overlay had no lock, so the wheel reached the dashboard
  // underneath: to get to the bottom of the deck you scrolled the PAGE, and the
  // modal — being fixed — stayed where it was while its own content ran off the
  // bottom of the screen. The deck is long enough that this was most of it.
  //
  // 🔑 THE SAME THREE LINES THE PROCESS MODAL IN THIS FILE ALREADY USES, and
  // Escape closes for the same reason it does there. Not a new mechanism.
  useEffect(() => {
    if (!trainingOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setTrainingOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [trainingOpen]);
  // Post-onboarding tasks (agreement + banking) — required before first payout.
  const [taskInfo, setTaskInfo] = useState(null);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  // Onboarding completion survey
  const [showOnboardingSurvey, setShowOnboardingSurvey] = useState(false);
  const [obRating, setObRating] = useState(0);
  const [obFeedback, setObFeedback] = useState('');
  const [obSubmitting, setObSubmitting] = useState(false);
  const [obDone, setObDone] = useState(false);
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Show onboarding survey on first dashboard visit
  // 🚨 IT NEVER SHOWED (broker report 1102, Jesse 2026-11-02). It read partner.onboarding_status, and `partner` is the
  // decoded sign-in token — which has never carried onboarding_status (id, email, names, partner_type only; the same
  // since the survey shipped in 95dfb9a4). So the check was always false. 🔑 The status is the SERVER's, read by this
  // page's GET /onboarding (taskInfo — the same figure the readiness gate uses). Intent unchanged: once the partner is
  // active, on the first dashboard visit, once per partner in this browser.
  // (the same server figure the badge and the not-active banner below read — push C fixed those the same way)
  const surveyStatus = taskInfo ? taskInfo.onboarding_status : null;
  useEffect(() => {
    if (!partner?.id || !surveyStatus) return undefined;
    const key = `doorlypro_onboarding_survey_shown_${partner.id}`;
    if (localStorage.getItem(key)) return undefined;
    if (surveyStatus !== 'active') return undefined;
    // 🔑 ONLY A PARTNER ACTIVATED AFTER THIS DEPLOY (Hannah, 2026-11-02): the server decides from the stored activation
    // time and the deploy's cut-over (backend utils/partnerOnboardingSurvey). Partners already active never see it.
    if (taskInfo.onboarding_survey_eligible !== true) return undefined;
    const t = setTimeout(() => { setShowOnboardingSurvey(true); localStorage.setItem(key, '1'); }, 800);
    return () => clearTimeout(t);
  }, [partner?.id, surveyStatus, taskInfo?.onboarding_survey_eligible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    const dismissed = localStorage.getItem('partner_survey_dismissed');
    if (dismissed && Date.now() - new Date(dismissed).getTime() < 7 * 86400000) return;
    setTimeout(() => {
      axios.get(`${API_URL}/api/doorlypro/survey/pending`, { headers: authHeaders })
        .then(r => { if (r.data?.has_pending) setShowSurvey(true); })
        .catch(() => {});
    }, 3000);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️ CALENDLY IS GONE FROM THE PORTAL, DELIBERATELY. "Book a Call" here opened
  // a booking link for a specific person, which meant an activated partner with a
  // question waited for a calendar slot to get an answer Eva has immediately —
  // and it routed around Partner Success entirely, so the rep who owns the
  // account never saw that the partner had asked.
  //
  // The Calendly <script> and <link> injection that used to sit here went with it:
  // it loaded a third-party widget into every partner session whether or not the
  // button was ever clicked.
  //
  // ⚠️ THE PUBLIC LANDING PAGE KEEPS ITS CALENDLY, AND THAT IS NOT AN OVERSIGHT.
  // pages/DoorlyProLanding.jsx is pre-login: a prospective partner booking a sales
  // call has no account, no rep and no Eva. Different audience, different need.

  useEffect(() => {
    // ⚠️ NO TOKEN IS A SETTLED ANSWER, not a reason to wait. Without this the
    // page would sit on its loading state for a signed-out render.
    if (!token) { setLoaded({ lists: true, tasks: true, firm: true }); return; }
    // ⚠️ THE ALERTS FETCH IS GONE WITH THE CARD IT FED. Nothing else on this
    // page read it, and a request whose only reader has been removed is a
    // round trip on every dashboard load for nobody. The Alerts page in the
    // left nav fetches its own.
    // ── 🚨 AN ORIGINATOR'S NUMBERS COME FROM THEIR LOAN FILES ──────────────
    //
    // Every KPI on this page read GET /referrals, which selects from
    // buyer_referrals. A loan officer CANNOT HAVE A ROW THERE —
    // referralRefusalForType refuses an LO referral, because referralCommission
    // would owe them the origination rate on the loan amount for an
    // introduction with no origination in it. So "Buyers Submitted",
    // "In Progress" and "Homes Closed" read 0, 0, 0 for every loan officer
    // forever, however many files they had sent.
    //
    // 🔑 THE TWO LISTS HAVE THE SAME SHAPE WHERE IT MATTERS. Both rows carry
    // partner_status, which is what isInProgress() and getStatusMeta() read, so
    // the derivations below are unchanged — only the source moves.
    //
    // ⚠️ AND IT WAITS FOR THE TYPE. partner arrives from the auth context and is
    // null on the first render; firing before it resolves would ask the realtor
    // endpoint for an originator's numbers and cache the zeroes.
    if (!partner?.partner_type) return;
    const listPath = partner.partner_type === 'loan_officer' ? 'borrowers' : 'referrals';
    axios.get(`${API_URL}/api/doorlypro/${listPath}`, { headers: authHeaders })
      .then((refRes) => setReferrals(
        refRes.data?.borrowers || refRes.data?.referrals || refRes.data || []))
      .catch(() => {}).finally(() => setLoaded((p) => ({ ...p, lists: true })));
    axios.get(`${API_URL}/api/doorlypro/me/file-counts`, { headers: authHeaders })
      .then((r) => setFileCounts(r.data && Number.isFinite(Number(r.data.files)) ? r.data : null))
      .catch(() => setFileCounts(null));
  }, [token, partner?.partner_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Agreement + banking completion (for the post-onboarding task cards).
  //
  // ⚠️ EXTRACTED SO THE RESEND CAN RE-READ IT. The task row carries the invite's
  // state, so an action that changes that state has to refetch or the row goes on
  // describing the world before the click.
  const loadTasks = useCallback(() => {
    if (!token) { setLoaded((p) => ({ ...p, tasks: true })); return Promise.resolve(); }
    return axios.get(`${API_URL}/api/doorlypro/onboarding`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTaskInfo(r.data))
      .catch(() => {})
      .finally(() => setLoaded((p) => ({ ...p, tasks: true })));
  }, [token]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  useEffect(() => {
    if (!token) { setLoaded((p) => ({ ...p, role: true })); return; }
    axios.get(`${API_URL}/api/doorlypro/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setIsAccountHolder(r.data?.partner?.account_role === 'broker'))
      .catch(() => setIsAccountHolder(false))
      .finally(() => setLoaded((p) => ({ ...p, role: true })));
  }, [token]);

  // ── 🔑 THE FIRM QUESTION, ASKED WHERE THE PARTNER NOW LANDS ──────────────
  //
  // 🚨 IT USED TO LIVE ON THE PROFILE STEP, which a new partner no longer passes
  // through. The resolver still refuses to settle an ambiguous match on its own —
  // account 11 "JLA" holds two Sales Agents with different sponsoring brokers
  // because a name match once attached without asking — so the question has to be
  // put somewhere, and this is where they are.
  //
  // ⚠️ IT RENDERS ONLY WHEN THERE IS SOMETHING TO ASK. A partner already on a firm
  // gets null and sees nothing.
  const [firmMatch, setFirmMatch] = useState(null);
  const [firmBusy, setFirmBusy] = useState(false);
  useEffect(() => {
    if (!token) { setLoaded((p) => ({ ...p, firm: true })); return; }
    axios.get(`${API_URL}/api/doorlypro/me/account-match`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setFirmMatch(r.data?.accountMatch || null))
      .catch(() => setFirmMatch(null))
      .finally(() => setLoaded((p) => ({ ...p, firm: true })));
  }, [token]);
  const answerFirmMatch = async (accountId) => {
    setFirmBusy(true);
    try {
      await axios.post(`${API_URL}/api/doorlypro/me/account-match`,
        accountId ? { account_id: accountId }
                  : { decline: true, account_id: (firmMatch?.candidates?.[0] || {}).id },
        { headers: { Authorization: `Bearer ${token}` } });
      setFirmMatch(null);
      // ⚠️ THE TASKS ARE REFETCHED, because attaching to a firm changes several of
      // them at once — the W-9 appears for a broker, banking and the counter-
      // signature learn whose they are.
      await loadTasks();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not save that. Try again.');
    }
    setFirmBusy(false);
  };

  // 🔑 RESEND, NOT RE-INVITE. The server mails the EXISTING invite; sending a new
  // one would be refused by the partial unique index and, if it were not, would
  // leave two pending rows for one person.
  const [resending, setResending] = useState(false);
  const [resentTo, setResentTo] = useState(null);
  const resendBrokerInvite = async () => {
    if (resending) return;
    setResending(true);
    try {
      const r = await axios.post(`${API_URL}/api/doorlypro/me/broker-invite/resend`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setResentTo(r.data?.email || true);
      await loadTasks();
      // ⚠️ THE CONFIRMATION IS TEMPORARY, because the button returns to being the
      // live action. A permanent "Sent ✓" would leave no way to send again.
      setTimeout(() => setResentTo(null), 6000);
    } catch (e) {
      // ⚠️ SURFACED, NOT SWALLOWED. The whole point of this button is that the
      // partner could not tell whether the first send worked.
      alert(e.response?.data?.error || 'Could not resend that invitation.');
    }
    setResending(false);
  };

  // ARCHIVED is terminal, not "setup incomplete". The old isNotActive caught it
  // only as a side effect of not being 'active', and then told a retired partner
  // their setup was incomplete and linked them back into onboarding — a flow
  // they should not re-enter. Same shape as the borrower terminal-state bug.
  // 🚨 THE STATUS IS THE SERVER'S, NOT THE TOKEN'S (push C). This read
  // partner.onboarding_status from the decoded JWT, which has never carried it,
  // so the "not active yet" banner never rendered and the badge below was
  // hard-coded. GET /onboarding now returns the shared readiness (healed).
  const readiness = taskInfo?.readiness || null;
  const serverStatus = readiness?.status || taskInfo?.onboarding_status || null;
  const isArchived = (serverStatus || partner?.onboarding_status) === 'archived';
  // 🚨 EXPLICIT, NOT "NOT A REALTOR". An unset type must not be handed an
  // originator's page — the Quick Action below routes on this.
  const isLoanOfficer = partner?.partner_type === 'loan_officer';
  const isNotActive = !isArchived && !!readiness && readiness.ready === false;
  // P6 (a): the badge — readiness, else the stored onboarding_status once loaded; nothing while loading.
  const badgeLabel = statusBadgeLabel({ loading: !loaded.tasks, readiness: readiness ? { ...readiness, status: isArchived ? 'archived' : readiness.status } : null,
    storedStatus: taskInfo?.onboarding_status || partner?.onboarding_status || null });
  // 🚨 EVERYTHING DONE, WAITING ON DOORLY. The partner finished profile and
  // training and the register has not confirmed their license yet. The copy is
  // the server's — one sentence, in one place — so this screen and Eva cannot
  // say different things about the same wait.
  const licensePending = taskInfo?.task_state?.state === 'license_pending';
  const licensePendingCopy = taskInfo?.task_state?.notice || '';

  // Post-onboarding tasks: realtors/LOs must sign the agreement and add banking
  // before their first fee payout. Shown once onboarding is complete.
  const onboardingDone = taskInfo?.onboarding_status === 'active';
  // READ the server-computed task state; do not recompute it. utils/partnerTasks.js
  // is the single source Eva reads too, and test/partnerTaskParity.test.js fails
  // if this card and Eva ever disagree. Recomputing here is exactly the drift the
  // parity test exists to prevent.
  const taskState = taskInfo?.task_state;
  // ⚠️ RENDERED FROM THE SERVER LIST, NOT FROM NAMED KEYS. The previous version
  // hardcoded two JSX blocks for agreement and banking, so adding a task to
  // utils/partnerTasks.js changed nothing on screen — the same failure shape as
  // HeroSubmitted's checklist that could never turn green. Every item below is a
  // row the server said was outstanding, with the column it reads named there.
  const outstandingTasks = (() => {
    // 🚨 'license_pending' CARRIES THE SAME ROWS. It used to arrive with
    // tasks: [] — so the moment a partner finished training, My Tasks vanished
    // and took the profile, the firm's banking, the W-9 and the
    // counter-signature with it. Those are not activation conditions and they
    // do not stop being outstanding because Doorly is reading a register.
    if (taskState?.state !== 'tasks' && taskState?.state !== 'license_pending') return [];
    const open = taskState.tasks.filter((t) => !t.done);
    // 🔑 TRAINING FIRST UNTIL IT IS DONE. Everything else on this list asks a
    // partner to act on terms the training is what explains — sign the agreement,
    // invite the broker who counter-signs it, supply the firm's banking. Ordering
    // it anywhere else asks them to do the work before they have been told what
    // it means.
    //
    // ⚠️ A RE-SORT, NOT A SECOND SOURCE. The rows and their wording still come
    // from utils/partnerTasks.js — this moves one of them, so a task added there
    // still appears here without any edit.
    if (!onboardingDone) {
      const i = open.findIndex((t) => t.key === 'training');
      if (i > 0) return [open[i], ...open.slice(0, i), ...open.slice(i + 1)];
    }
    return open;
  })();
  // 🚨 THE TASK LIST NO LONGER WAITS FOR ACTIVATION, AND THAT IS THE POINT OF THE
  // CHANGE. It rendered only once onboarding_status was 'active' — which training
  // completion sets — so a partner who had just registered landed on a dashboard
  // with NO tasks on it and a banner telling them to go somewhere else. The list
  // that says "start your training" was hidden until they had finished the
  // training.
  //
  // ⚠️ onboarding_status ITSELF IS UNCHANGED. It is what every partner count, the
  // Partners page status pill and the admin KPIs read, so moving when it flips to
  // 'active' would quietly restate every one of those numbers. What changed is
  // that the dashboard stops treating it as permission to show a to-do list.
  const showTasks = !isArchived && outstandingTasks.length > 0;
  // Per-task CTA wording. A task whose key is not listed still renders — it just
  // gets the generic verb, so a new server task can never silently vanish here.
  const TASK_CTA = {
    agreement: 'Sign now', banking: 'Add banking', profile: 'Complete profile',
    // 🚨 NO license ENTRY. The task is gone — licence evidence comes from the
    // registry, and neither registry check is something a partner can perform.
    // A CTA for a task the server no longer sends is a wording nobody can reach.
    w9: 'Upload',
    // 🚨 NO training ENTRY. The training row is caught by its own branch below,
    // which uses TRAINING_CTA so the wording can reflect progress — a copy here
    // would be unreachable, and an unreachable wording for a live button is the
    // one a later reader edits believing it is the one on screen.
    // ⚠️ THE ONLY CASE WHERE A CTA EXISTS FOR A TASK THAT IS NOT THE PARTNER'S.
    // They cannot counter-sign — but if their firm has no broker at all, they can
    // put one there, and the server sends `where` only in that case.
    countersigned: 'Invite your broker',
  };
  // 🚨 WHICH ITEMS ACTUALLY HOLD MONEY, mirrored from PAYOUT_REQUIREMENTS in
  // backend/utils/partnerTasks.js — the same list routes/agents.js refuses with.
  // There is no import path between frontend/ and backend/, so this is a checked
  // copy: test/partnerTaskParity.test.js compares the two.
  const PAYOUT_BLOCKING_KEYS = ['agreement', 'countersigned', 'banking'];
  // 🔑 ONE WIDTH FOR EVERY TASK CTA. The verbs differ in length — "Sign now",
  // "Complete profile", "Invite broker", "Start training" — and each button was
  // sized to its own text, so the right-hand edge of the card zig-zagged down the
  // list. A fixed width makes the column read as a column; the label inside is
  // still whatever the server sent.
  //
  // ⚠️ minWidth RATHER THAN width, so a longer verb added later grows the button
  // instead of being clipped — and the no-button case keeps the same footprint so
  // rows without one do not shift the others.
  // ── 🔑 HOW FAR THROUGH THE DECK THEY ARE ─────────────────────────────────
  //
  // "Start training" is a lie to somebody four modules in, and it is the reason
  // they would not click it: it reads as though their progress is gone.
  //
  // ⚠️ THE SAME modulesForPartner CALL THE MODAL MAKES, so the count is out of
  // the deck this partner will actually be shown. A broker gets the firm module
  // prepended, so "3 of 6" and "3 of 5" are different sentences about the same
  // person and only one of them is true.
  //
  // ⚠️ PER-DEVICE, AND THAT IS CONSISTENT RATHER THAN CORRECT. Progress lives in
  // localStorage, so a partner who read three modules on their phone sees
  // "Start training" on their laptop — but the deck they then open shows no
  // checkmarks either, so the button and the deck agree. Moving progress to the
  // server is its own piece of work; a label that disagreed with the deck beside
  // it would be worse than one that is merely local.
  const training = trainingProgress({
    partnerId: partner?.id,
    modules: modulesForPartner({ partnerType: partner?.partner_type, isBroker: isAccountHolder }),
  });
  // 🚨 complete HERE MEANS "EVERY MODULE READ", NOT "TRAINING RECORDED". The
  // server owns completion (POST /me/training), and this task row only renders
  // while the server still says it is outstanding — so a partner who read
  // everything but whose submit never landed is told to FINISH, which is
  // precisely what is left for them to do.
  // ── 🚨 THE COUNT IS NOT IN THE LABEL ────────────────────────────────────
  //
  // It was: "Resume training · 2/5". CTA_STYLE gives every task button the same
  // minWidth so the right-hand edge of the column reads as a column — but
  // minWidth is a floor, and the extra characters pushed this one past it. One
  // button wider than the rest down a list of otherwise identical buttons is
  // the ragged edge CTA_STYLE exists to prevent, reintroduced by the thing
  // inside it.
  //
  // 🔑 SO THE PROGRESS SITS OUTSIDE, to the right. It is a status about the
  // task, not part of the action — "Resume training" is what the button does and
  // "2/5" is where they are — and separating them keeps every CTA one width.
  const TRAINING_CTA = training.complete ? 'Finish training'
    : training.started ? 'Resume training'
      : 'Start training';
  const CTA_STYLE = {
    flexShrink: 0, minWidth: 150, textAlign: 'center', padding: '8px 16px',
    background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };
  // 🔑 ONE COUNT WITH DOORLYDESK (Jesse, 2026-11-02): the shared server count when it answered; the list length only
  // if it did not. The label stays the existing "Loan Files Sent" / "Buyers Submitted".
  const totalReferrals = fileCounts ? Number(fileCounts.files) : referrals.length;
  // "In Progress" = actively-working buyers, derived from the partner label
  // (NOT raw status). Excludes Invited (not started) and terminal states.
  // Raw 'pending' now surfaces as the "Invite not yet accepted" label, so it no
  // longer counts.
  const activeReferrals = referrals.filter(r => isInProgress(r.partner_status)).length;
  // Label-derived so 'funded' buyers (which roll into the "Closed" label) are
  // counted, not just raw status === 'closed'. Same pattern as the In Progress KPI.
  const closedReferrals = referrals.filter(r => getStatusMeta(r.partner_status).label === 'Closed').length;
  // ⚠️ recentReferrals AND recentAlerts ARE GONE WITH THE CARDS THEY FED. The
  // full lists are still fetched because the KPI counts above are computed from
  // them; only the five-and-three excerpts have no reader left.

  // 🚨 `loading` NEVER GATED THE PAGE, WHICH IS WHY IT STILL FLASHED. It only
  // swapped KPI values to "—" and spun two cards, inside a layout that was already
  // fully painted — so the STRUCTURE went on changing underneath: the My Tasks
  // card appeared when the task list arrived, the firm prompt appeared above it,
  // and for an account holder the Recent Buyers and Recent Alerts cards vanished
  // when the role answer landed. Four reflows, none of which a value placeholder
  // can hide.
  //
  // 🔑 SO THE BODY WAITS AND A SKELETON HOLDS THE SPACE. The same treatment the
  // registration form and the sidebar got, for the same reason: a page whose
  // shape depends on a fetch has to know its shape before it is shown.
  if (loading) {
    return (
      <RequirePartnerAuth>
        <DoorlyProLayout>
          <div aria-busy="true" aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span className="sr-only">Loading your dashboard…</span>
            <div style={{ height: 96, borderRadius: 12, background: '#f1f3f7' }} />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 14 : 20 }}>
              {[0, 1, 2].map((i) => <div key={i} style={{ height: 104, borderRadius: 12, background: '#f1f3f7' }} />)}
            </div>
            <div style={{ height: 220, borderRadius: 12, background: '#f1f3f7' }} />
          </div>
        </DoorlyProLayout>
      </RequirePartnerAuth>
    );
  }

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>

        {/* Onboarding banner */}
        {isArchived && (
          <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>This account is not currently active</div>
            <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
              Your DoorlyPro account is archived, so you can&rsquo;t submit new buyers right now. Your
              past referrals remain visible. If you&rsquo;d like to reactivate, message the Doorly team
              and someone will help.
            </div>
          </div>
        )}

        {/* 🚨 NOT A TASK, AND NOT AN ERROR. There is nothing on this screen for
            them to press, because there is nothing for them to do — the work is
            Doorly's. No fee language, and nothing that reads as a problem with
            their paperwork. */}
        {licensePending && licensePendingCopy && (
          <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
            padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a8a', marginBottom: 4 }}>
              You&rsquo;re all set &mdash; one last check on our side
            </div>
            <div style={{ fontSize: 13, color: '#1e40af', lineHeight: 1.6 }}>
              {licensePendingCopy}
            </div>
          </div>
        )}

        {/* ⚠️ THE BANNER NO LONGER POINTS AWAY FROM THE LIST BENEATH IT. It said
            "finish onboarding" with a link to another screen, while My Tasks —
            now directly below — holds the same items with their own buttons.
            Two routes to one job, one of which leaves the page, is how a partner
            ends up unsure which is the real one. It states the position and lets
            the list be the action. */}
        {/* ⚠️ ABOVE THE TASK LIST, because until it is answered several of those
            tasks cannot say whose they are. */}
        {!isArchived && firmMatch && (
          <div style={{ marginBottom: 20 }}>
            <FirmMatchPrompt
              question={firmMatch.question}
              companyName={firmMatch.companyName}
              candidates={firmMatch.candidates}
              registry={firmMatch.registry}
              busy={firmBusy}
              onJoin={(id) => answerFirmMatch(id)}
              onCreate={() => answerFirmMatch(null)}
            />
          </div>
        )}

        {isNotActive && (
          <div data-testid="dashboard-not-active" style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
              Your account is not active yet. Here is what is left:
            </div>
            {/* 🔑 THE SAME LIST THE SUBMIT PAGE REFUSES WITH (push C). */}
            <div style={{ background: '#fff', borderRadius: 8, padding: '0 12px' }}>
              <ReadinessChecklist missing={readiness.missing} notice={readiness.notice}
                returnTo={returnTo || '/doorlypro/dashboard'} testId="dashboard-readiness" />
            </div>
          </div>
        )}


        {/* ── WELCOME BAR ── */}
        <div style={{ background: '#3a6ad9', borderRadius: '14px', padding: isMobile ? '16px' : '22px 28px', marginBottom: isMobile ? '16px' : '24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {(() => {
              const ini = [partner?.first_name?.[0], partner?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?';
              const pic = partner?.profile_picture_url;
              const stored = JSON.parse(localStorage.getItem('doorlypro_partner') || '{}');
              const url = pic || stored.profile_picture_url;
              return url ? (
                <img src={url} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{ini}</div>
              );
            })()}
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                DoorlyPro Partner
              </div>
              <h1 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#fff', lineHeight: 1.2 }}>
                Welcome back, {partner?.first_name || 'Partner'}
              </h1>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* 🚨 NOT HARD-CODED ANY MORE (push C): every partner saw "✓ Active Partner", including the ones
                DoorlyDesk showed as Pending and the Submit page refused. P6 (a), Jesse 2026-11-01: no "DoorlyPro
                Partner" fallback — the stored status when readiness is missing, and no badge while loading
                (utils/partnerStatusBadge). */}
            {badgeLabel && (
              <span data-testid="dashboard-status-badge" style={{ fontSize: '12px', fontWeight: '700', padding: '5px 12px', borderRadius: '20px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}>
                {badgeLabel}
              </span>
            )}
            {/* 🚨 THE SAME WRONG DOOR AS THE QUICK ACTION, AND MORE PROMINENT.
                This sent every partner to the realtor referral form. An LO who
                filed one would be owed the ORIGINATION rate on the loan amount
                for an introduction with no origination in it, which is why
                referralRefusalForType refuses it — so the button led to a
                refusal at best. Third instance of this mapping being half
                closed. */}
            <button onClick={() => navigate(isLoanOfficer
              ? '/doorlypro/submissions' : '/doorlypro/referrals/new')}
              style={{ padding: '8px 18px', background: '#fffd5b', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '13px' }}>
              {isLoanOfficer ? '+ Submit an Application' : '+ Submit a Buyer'}
            </button>
          </div>
        </div>
        {/* 🔑 THE KPIs SIT ABOVE MY TASKS. They were below the task card and the
            broker prompt, so a partner with outstanding setup — which is most of
            them, and every new one — had to scroll past two warning-coloured
            panels to see how many buyers they had submitted. The numbers are the
            reason they opened the page; the tasks are what they do next. */}
        {/* ── KPI CARDS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '10px' : '14px', marginBottom: isMobile ? '16px' : '24px' }}>
          {[
            { iconName: 'users', label: isLoanOfficer ? 'Loan Files Sent' : 'Buyers Submitted',
              value: loading ? '—' : totalReferrals, color: '#3a6ad9', sub: 'All time' },
            { iconName: 'clock', label: 'In Progress', value: loading ? '—' : activeReferrals, color: '#ff082f', sub: 'Currently active' },
            { iconName: 'check-circle', label: 'Homes Closed', value: loading ? '—' : closedReferrals, color: '#acf44e', sub: 'Successfully closed' },
          ].map(kpi => (
            <div key={kpi.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Icon name={kpi.iconName} size={22} color={kpi.color} />
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{kpi.label}</div>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '900', color: kpi.color, lineHeight: 1, marginBottom: '4px' }}>{kpi.value}</div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── My Tasks — outstanding setup, below the welcome bar ── */}
        {showTasks && (
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #fcd34d', borderRadius: 12, padding: isMobile ? '14px' : '16px 20px', marginBottom: isMobile ? 16 : 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Icon name="warning" size={16} color="#d97706" />
              <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>My Tasks</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 100, padding: '2px 9px' }}>
                {outstandingTasks.length}
              </span>
            </div>
            {/* 🔑 THE PAYOUT LINE IS THE PAYOUT CHECK'S OWN LIST (Jesse 2026-11-01): utils/partnerPayoutReadiness holds a fee
                until five things are in place — a payee firm, the agreement, the broker's counter-signature where it
                applies, banking and a W-9 (one check since Hotfix A). The server sends which are open (GET /onboarding
                payout); this names them. Unreadable → nothing said about payment. */}
            {taskInfo && taskInfo.payout && (
              <p data-testid="dashboard-payout-line" style={{ margin: '0 0 12px', fontSize: 12.5, color: '#6b7280' }}>
                {payoutLine(taskInfo.payout.missing)}
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {outstandingTasks.map((t) => (
                <div key={t.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#f9fafb' }}>
                  <div>
                    {/* The label comes from the server, which is why the profile
                        item can say WHICH fields are missing rather than just
                        "incomplete". */}
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>{t.label}</div>
                    {/* 🚨 SOURCED FROM THE SERVER'S PAYOUT LIST, NOT FROM A HARDCODED
                        PAIR OF KEYS. This read (t.key === 'agreement' || t.key ===
                        'banking') and so silently omitted the counter-signature the
                        day it started gating payout — the one blocker a partner is
                        least likely to guess at, rendered as ordinary housekeeping. */}
                    {PAYOUT_BLOCKING_KEYS.includes(t.key) && (
                      <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>Required before your first payout.</div>
                    )}
                    {/* 🔑 THE INVITE IS OUT, SAID AS A STATUS AND NOT ONLY IN PROSE.
                        A partner who has just sent the invitation needs to see that
                        it landed; without this the row read "Invite broker" again,
                        which looks like the send failed. */}
                    {t.invite_pending && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#7a5200', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '2px 8px' }}>
                          Pending
                        </span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          Sent to {t.invite_email}
                          {t.invite_sent_at ? ` · ${new Date(t.invite_sent_at).toLocaleDateString()}` : ''}
                        </span>
                      </div>
                    )}
                    {/* ⚠️ THE DETAIL IS WHERE "this one is not yours" LIVES. Without
                        it a partner reads a red row about their own agreement and
                        goes looking for a button that does not exist for them. */}
                    {t.detail && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.5, maxWidth: 560 }}>{t.detail}</div>
                    )}
                  </div>
                  {/* ⚠️ NO BUTTON WHEN THERE IS NOWHERE TO GO. navigate(null) throws,
                      and a button that cannot help is worse than none. */}
                  {/* 🚨 THE TASK'S OWN VERB WINS OVER THE KEY'S. TASK_CTA maps
                      key -> verb, so the banking row rendered "Add banking" to a
                      partner being told in the same breath that it is NOT theirs
                      to add — a button pointing at a form the server refuses. The
                      server knows which situation this is and now says so.

                      ⚠️ AND THIS COMMENT SITS ABOVE THE TERNARY, NOT INSIDE IT.
                      A JSX comment as the first thing in a ternary branch is
                      object-literal syntax, not a child — it parses as an object
                      with a comment in it and fails on the next token. */}
                  {/* 🚨 "Invite broker" POINTED AT THIS VERY PAGE, so clicking it
                      navigated nowhere and looked broken — on the two rows that
                      tell a partner their pay is held. A task whose destination
                      is the current page has to act, not route. */}
                  {t.key === 'training' ? (
                    // 🔑 GREEN ONCE THEY ARE UNDER WAY, blue while it is still an
                    // unstarted task. #acf44e is the brand green the deck's own
                    // "Complete Module" button uses, with dark text because the
                    // white that works on #3a6ad9 is unreadable on it.
                    //
                    // ⚠️ THE SERVER'S cta STILL WINS. TASK_CTA has always been the
                    // fallback for a label the server did not send, and a new
                    // server-side wording must not be overridden by a local guess
                    // about progress.
                    // ── 🚨 THE COUNT GOES TO THE LEFT, INSIDE ONE FLEX ITEM ──
                    //
                    // A fragment was wrong, and the comment that justified it
                    // was wrong with it. It claimed a wrapping div would "add a
                    // flex item and move the button off the shared right edge" —
                    // the opposite is true. The row is
                    // justifyContent: space-between with exactly TWO children
                    // (the text block, the CTA), so a fragment made the count a
                    // THIRD child: space-between then pushed the count to the
                    // right edge and the BUTTON inward, which is precisely the
                    // misalignment being reported.
                    //
                    // 🔑 ONE WRAPPER KEEPS THE ROW AT TWO CHILDREN, so the
                    // wrapper's right edge lands where every other row's CTA
                    // sits and the button inside it keeps CTA_STYLE's width. The
                    // count sits to its left.
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                      flexShrink: 0 }}>
                      {/* 🔑 ONLY WHILE IT MEANS SOMETHING. "0/8 Completed"
                          before they start is noise, and on a finished deck it
                          would say eight of eight about a task the server still
                          lists as open — a contradiction rather than "your
                          submit did not land", which the label carries instead
                          as "Finish training". */}
                      {training.started && !training.complete && (
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#6b7280',
                          whiteSpace: 'nowrap' }}>
                          {training.done}/{training.total} Completed
                        </span>
                      )}
                      <button onClick={() => setTrainingOpen(true)}
                        style={training.started
                          ? { ...CTA_STYLE, background: '#acf44e', color: '#1a1a1a', fontWeight: 800 }
                          : CTA_STYLE}>
                        {t.cta || TRAINING_CTA}
                      </button>
                    </div>
                  ) : t.cta === 'Resend invitation' ? (
                    <button onClick={() => resendBrokerInvite()} disabled={resending}
                      style={{ ...CTA_STYLE, opacity: resending ? 0.6 : 1 }}>
                      {resending ? 'Sending…' : (resentTo ? 'Sent ✓' : t.cta)}
                    </button>
                  ) : t.cta === 'Invite broker' ? (
                    <button onClick={() => setBrokerOpen((n) => n + 1)} style={CTA_STYLE}>
                      {t.cta}
                    </button>
                  ) : t.where ? (
                    <button onClick={() => navigate(t.where)} style={CTA_STYLE}>
                      {t.cta || TASK_CTA[t.key] || 'Open'}
                    </button>
                  ) : (
                    // ⚠️ THE SAME FOOTPRINT AS A BUTTON — a narrower cell pulled
                    // the rows either side out of line, which is the ragged edge
                    // CTA_STYLE exists to fix. Written as a LINE comment: a JSX
                    // comment first in a ternary branch is an object literal, and
                    // this is the second time that has broken a build today.
                    <div style={{ flexShrink: 0, minWidth: 150, textAlign: 'center', fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>
                      {t.not_yours ? 'Not yours to do' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── 🚨 INVITE YOUR BROKER ────────────────────────────────────────
            This component existed and was mounted ONLY on Documents, so a
            partner whose pay is held for a counter-signature and whose firm has
            no banking had no way to act from the screen that tells them both.
            Two tasks pointed here; nothing here pointed anywhere.

            ⚠️ IT RENDERS ITSELF OR NOTHING. The server answers `needed` — a firm
            with a broker, or an account-level holder, gets no card — so this is
            placed unconditionally and stays silent when it does not apply. */}
        {/* ── 🚨 THE LICENCE DOCUMENT, WHEN THE REGISTER SAID NO ───────────
            It renders itself or nothing: the server answers with a request only
            for the four negative outcomes, and with null for a pass, a pending
            reading, a source outage, a missing number and a disciplinary
            status — every case where a document cannot help. A partner whose
            licence checks out sees nothing at all. */}
        {!isArchived && <LicenseEvidenceCard token={token} onChange={loadTasks} />}

        {/* 🚨 NOT GATED ON ACTIVATION ANY MORE, AND THAT WAS THE BUG. The task
            list above deliberately renders before a partner is active — it is
            what tells a new partner to start their training — so it carried a
            live "Invite broker" button while this component, which owns the
            dialog that button opens, was mounted only for an ACTIVE partner.
            Partner #59 sits in training_pending with that button on screen
            today. The component already renders itself or nothing from the
            server's `needed`, so the extra gate added no safety and one dead
            button. */}
        {!isArchived && <BrokerNomination token={token} openSignal={brokerOpen} />}

        {/* ── QUICK ACTIONS ── */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Quick Actions</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '10px' : '12px' }}>
            {[
              { iconName: 'list',  label: 'The Doorly Process', desc: 'See how it works', onClick: () => setShowProcessModal(true), primary: false },
              // ── 🚨 A WRONG DOOR WITH A PAYMENT BEHIND IT ──────────────────
              //
              // This card read "My Buyers" and pointed at /doorlypro/referrals
              // for EVERY partner — the realtor page, whose own action is
              // "Submit a Buyer". An originator following it lands on a referral
              // form they must not file: referralRefusalForType refuses it, and
              // the nav's comment on My Buyers says why — an LO who filed one
              // would be owed the ORIGINATION rate on the loan amount for an
              // introduction with no origination in it.
              //
              // ⚠️ THE NAV ALREADY HID My Buyers FROM THEM. The Quick Action was
              // the half of that mapping nobody closed, which is exactly the
              // failure that comment warns about: "half a mapping reads as a
              // rule and leaves the opposite door open."
              isLoanOfficer
                ? { iconName: 'users', label: 'My Borrowers', desc: 'Your loan files and where they are', path: '/doorlypro/borrowers', primary: false }
                : { iconName: 'users', label: 'My Buyers', desc: 'View all submitted buyers', path: '/doorlypro/referrals', primary: false },
              { iconName: 'chart', label: 'Borrower Journey', desc: 'What each status means & next', path: '/doorlypro/borrower-journey', primary: false },
              // FAQs removed — it duplicated the Help centre link now in the left
              // nav, and a Quick Action that just opens an external site was the
              // odd one out among four in-app actions.
                { iconName: 'search', label: 'Check Eligibility',
                desc: isLoanOfficer ? 'See if your borrower qualifies' : 'See if your buyer qualifies',
                path: '/doorlypro/eligibility', primary: false },
            ].map(a => (
              <div key={a.label} onClick={() => {
                if (a.onClick) return a.onClick();
                if (a.external) return window.open(a.path, '_blank');
                navigate(a.path);
              }}
                style={{ background: a.primary ? '#3a6ad9' : '#fff', border: `1px solid ${a.primary ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: '12px', padding: '18px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'transform 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}>
                <div style={{ marginBottom: '10px' }}><Icon name={a.iconName} size={24} color={a.primary ? '#fff' : '#374151'} /></div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: a.primary ? '#fff' : '#111827', marginBottom: '4px' }}>{a.label}</div>
                <div style={{ fontSize: '12px', color: a.primary ? 'rgba(255,255,255,0.75)' : '#9ca3af', lineHeight: '1.4' }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 🚨 RECENT BUYERS + RECENT ALERTS ARE GONE, FOR EVERYBODY ──────
            They were hidden for an ACCOUNT HOLDER on 17 Sep, on the ground that
            both describe the reader's own production while a holder's dashboard
            is about the firm. Jesse's call, 2 Oct: remove them for members too.
            Each duplicated a page that owns the same data and renders more of
            it — the buyers list and the alerts page, both in the left nav — and
            a short, stale copy of a list beside the real one is the thing that
            goes out of date first.

            ⚠️ THE DASHBOARD STILL SAYS WHAT IS HAPPENING. My Tasks, the
            licence notice, the firm-match prompt and the broker card all stay;
            what is removed is the pair of read-only excerpts. */}

        {/* ── HAVE QUESTIONS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: isMobile ? '16px' : '24px' }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '22px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ marginBottom: '12px' }}><Icon name="chat" size={28} color="#3a6ad9" /></div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>Have Questions?</div>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>Our partner team is here to help with anything — from submitting buyers to tracking deal progress.</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/doorlypro/messages')}
                style={{ padding: '9px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Icon name="chat" size={14} color="#fff" /> Message Us</button>
              {/* Doorly yellow. Text and icon go BLACK — #fff on #fffd5b is about
                  1.2:1 and effectively unreadable; black is ~15:1. Same pairing
                  as the Submit a Buyer button in the welcome bar. */}
              {/* Eva is mounted in DoorlyProLayout on every partner page; this
                  dispatches the same event the sidebar Help control does, rather
                  than mounting a second widget. */}
              <button onClick={() => window.dispatchEvent(new Event(OPEN_EVA_EVENT))}
                style={{ padding: '9px 18px', background: '#fffd5b', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                Ask Eva
              </button>
            </div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #3a6ad9, #2854c0)', borderRadius: '14px', padding: '22px', color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: '12px' }}><Icon name="arrow" size={28} color="#fff" /></div>
            {/* 🚨 THE FOURTH INSTANCE. Same realtor route, same realtor noun.
                ⚠️ AND THE COPY WAS WRONG FOR AN LO IN A SECOND WAY: "under 2
                minutes, just their basic info, Doorly takes it from there" is
                the REFERRAL promise. An originator sends a loan file — they
                took the application, and the form asks for what they already
                have. Telling them it is two minutes of basic info sets them up
                to meet a form that is neither. */}
            <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px' }}>
              {isLoanOfficer ? 'Ready to Submit a Loan File?' : 'Ready to Submit a Buyer?'}
            </div>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'rgba(255,255,255,0.8)', lineHeight: '1.6' }}>
              {isLoanOfficer
                ? 'Send the file as soon as you have taken the application — no property address '
                  + 'needed, and no waiting for your borrower to find a house.'
                : 'Submit a buyer in under 2 minutes. Just enter their basic info and Doorly takes it from there.'}
            </p>
            <button onClick={() => navigate(isLoanOfficer
              ? '/doorlypro/submissions' : '/doorlypro/referrals/new')}
              style={{ padding: '9px 20px', background: '#fffd5b', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '13px' }}>
              {isLoanOfficer ? 'Submit an Application →' : 'Submit a Buyer →'}
            </button>
          </div>
        </div>

        {/* Feedback */}
        <div style={{ textAlign: 'center' }}>
          <button onClick={() => setShowSurvey(true)}
            style={{ padding: '8px 16px', background: '#eff6ff', color: '#2563EB', border: '1px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
            <Icon name="star" size={14} color="#2563EB" style={{ marginRight: 4 }} /> Share Feedback
          </button>
        </div>

        {/* ── TRAINING, IN PLACE ──────────────────────────────────────────
            ⚠️ THE SAME COMPONENT THE ONBOARDING ROUTE RENDERS, so there is one
            deck. Progress shares the partner-keyed localStorage entry, and
            completion is the server's — onDone fires after POST /me/training
            succeeds, then the task list is re-read so the row disappears for the
            reason it actually disappeared. */}
        {trainingOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            {/* ⚠️ absolute, NOT fixed. A fixed backdrop inside the overlay is
                positioned against the viewport rather than its parent, so it
                could never be the thing the panel scrolls against — and it was
                the element the wheel landed on outside the panel, which is where
                the scroll went to the page. */}
            <div onClick={() => setTrainingOpen(false)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
            {/* 🔑 THE PANEL IS CAPPED AND THE SCROLL IS INSIDE IT. The overlay
                used to be the scroller (overflowY on a fixed, full-bleed flex
                container) with an uncapped panel inside it — so the panel grew
                past the viewport and the only way down was the page behind.
                maxHeight + a dedicated overflow region is the shape the process
                modal in this file already uses. */}
            <div style={{ position: 'relative', zIndex: 10001, background: '#fff',
              borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              {/* ⚠️ overscrollBehavior: 'contain' — without it, reaching the end
                  of this region hands the remaining scroll to the page, which is
                  the same symptom arriving a second way. */}
              <div style={{ padding: 24, overflowY: 'auto', overscrollBehavior: 'contain',
                flex: 1, minHeight: 0 }}>
                <PartnerTraining
                  partner={partner}
                  modules={modulesForPartner({ partnerType: partner?.partner_type,
                    isBroker: isAccountHolder })}
                  onDone={() => {
                    setTrainingOpen(false);
                    // 🔑 BACK TO WHERE SHE STARTED (push C), e.g. the Submit page.
                    if (returnTo) { window.location.href = returnTo; return; }
                    loadTasks();
                  }}
                  onExit={() => setTrainingOpen(false)}
                />
              </div>
            </div>
          </div>
        )}

        {showSurvey && (
          <PartnerSurveyModal
            onClose={() => { setShowSurvey(false); localStorage.setItem('partner_survey_dismissed', new Date().toISOString()); }}
            onSubmit={() => setTimeout(() => setShowSurvey(false), 2500)}
          />
        )}

        {/* The Doorly Process modal */}
        {showProcessModal && <DoorlyProcessModal onClose={() => setShowProcessModal(false)} partnerType={partner?.partner_type} />}

        {/* Onboarding Completion Survey */}
        {showOnboardingSurvey && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            {/* Backdrop */}
            <div onClick={() => setShowOnboardingSurvey(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />

            {/* Confetti */}
            <style>{`@keyframes confettiFall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
            {Array.from({ length: 60 }, (_, i) => {
              const colors = ['#fffd5b', '#3a6ad9', '#acf44e', '#ff082f', '#ffffff', '#ffdd86'];
              return <div key={i} style={{ position: 'fixed', left: Math.random() * 100 + '%', top: '-20px', width: Math.random() * 8 + 4, height: Math.random() * 8 + 4, background: colors[i % colors.length], borderRadius: Math.random() > 0.5 ? '50%' : '2px', animation: `confettiFall ${Math.random() * 2 + 2}s ${Math.random() * 1.5}s ease-in forwards`, zIndex: 10001, pointerEvents: 'none' }} />;
            })}

            {/* Modal */}
            <div style={{ position: 'relative', zIndex: 10002, background: '#fff', borderRadius: 20, padding: '36px 32px', maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center' }}>
              {/* Close button */}
              <button onClick={() => setShowOnboardingSurvey(false)} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1 }}>x</button>

              {obDone ? (
                <div>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🙏</div>
                  <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: '#111827' }}>Thank you!</h2>
                  <p style={{ margin: 0, fontSize: 15, color: '#6b7280' }}>Your feedback helps us build a better platform for partners like you.</p>
                  {isHighRating(obRating, 5) && (
                    <TrustpilotPrompt context="partner" onClose={() => setShowOnboardingSurvey(false)} />
                  )}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                  <h2 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
                    Congratulations —<br />You're now an official Doorly Partner!
                  </h2>
                  <p style={{ margin: '0 0 24px', fontSize: 15, color: '#6b7280', lineHeight: 1.6 }}>
                    Your account is set up and ready to go. Take a few seconds to share your feedback on the onboarding experience.
                  </p>

                  {/* Star rating */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      How would you rate the onboarding experience?
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} type="button" onClick={() => setObRating(n)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 32, padding: 2, filter: n <= obRating ? 'none' : 'grayscale(100%) opacity(0.3)', transition: 'all 0.15s', transform: n <= obRating ? 'scale(1.1)' : 'scale(1)' }}>
                          ⭐
                        </button>
                      ))}
                    </div>
                    {obRating > 0 && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{obRating}/5</div>}
                  </div>

                  {/* Feedback text */}
                  <div style={{ marginBottom: 24, textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      Was there anything about the onboarding process we could improve? <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
                    </div>
                    <textarea value={obFeedback} onChange={e => setObFeedback(e.target.value)}
                      placeholder="How was the registration and onboarding process?"
                      rows={3}
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => setShowOnboardingSurvey(false)}
                      style={{ flex: 1, padding: '12px', background: '#f3f4f6', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#6b7280' }}>
                      Skip
                    </button>
                    <button disabled={obSubmitting || obRating === 0} onClick={async () => {
                      setObSubmitting(true);
                      try {
                        await axios.post(`${API_URL}/api/doorlypro/survey/onboarding`, { rating: obRating, feedback: obFeedback }, { headers: authHeaders });
                      } catch {}
                      setObDone(true);
                      if (!isHighRating(obRating, 5)) setTimeout(() => setShowOnboardingSurvey(false), 2500);
                    }}
                      style={{ flex: 2, padding: '12px', background: obRating === 0 ? '#e5e7eb' : '#3a6ad9', color: obRating === 0 ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: obRating === 0 ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: 14 }}>
                      {obSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </DoorlyProLayout>
    {/* ⚠️ Eva now lives in DoorlyProLayout so she is on EVERY partner page and
        the sidebar Help control has something to open. Mounting her here too
        would put two widgets on the dashboard, both listening for the same open
        event. */}
    </RequirePartnerAuth>
  );
}

// ─── The Doorly Process modal ─────────────────────────────────────────────────
// Informational overlay. Mirrors the onboarding-survey modal pattern used
// elsewhere in this file: fixed full-screen wrapper, dark backdrop, centered
// white panel with rounded corners. Step 12 gets the green accent to signal
// post-closing completion.
// 🚨 THREE OF THESE STEPS ARE NOT THE SAME STORY FOR A REALTOR, AND THE
// DIFFERENCE IS WHO DOES THE HOME SELECTION.
//
// This list describes a buyer who self-serves: browses the MLS in the portal,
// requests showings through Doorly, and submits their own offer request. That is
// true for a self-registered buyer and for a loan-officer referral. It is FALSE
// for a realtor-referred buyer — Realtor Services Agreement v2.1 §2.1 makes
// property identification, showings and home-selection advice the REALTOR's
// work, and §4.2 conditions their fee on completing it before Doorly offers.
//
// ⚠️ SO THE REALTOR-DIVERGENT STEPS CARRY A `realtor` OVERRIDE RATHER THAN A
// SECOND LIST. A forked twelve-step array would drift on the nine steps that are
// identical, and the nine identical ones are the ones nobody would think to
// check. Only `desc` and `title` may differ; the sequence is the same sequence.
const DOORLY_PROCESS_STEPS = [
  { title: 'Partner Submits {CLIENT_TITLE}',
    desc: "You submit your {CLIENT}'s information through DoorlyPro and they receive an invitation to create their Doorly account. They are immediately prompted to begin their application. You can resend the invitation at any time from the My {CLIENT_TITLE}s page on your dashboard.",
    realtor: { title: 'You Submit Your Buyer',
      desc: "You submit your buyer through DoorlyPro — just the buyer; they do not need to have chosen a home yet. They receive an invitation to create their Doorly account and are prompted to begin their application. Submitting starts your work on that buyer rather than finishing it. You can resend the invitation at any time from the My Buyers page." } },
  { title: 'Borrower Application',
    desc: 'Your {CLIENT} completes their full application inside the Doorly portal, including personal information, income, employment, and assets.' },
  { title: 'Verification',
    desc: 'Your {CLIENT} verifies their identity and uploads supporting documents such as pay stubs, bank statements, and W-2s. They also have the option to complete verification automatically through integrations including Persona for identity and Plaid for income and asset verification.' },
  { title: 'Review & Approval',
    desc: 'Doorly reviews the application and documents and notifies your {CLIENT} if anything additional is needed. Once complete, your {CLIENT} receives their Approval status and their file moves forward.' },
  { title: 'SmartEstimate',
    desc: 'Your {CLIENT} is presented with their SmartEstimate, showing their purchase price, interest rate, loan term and total cash to close. Their price is Doorly\'s contract price with the seller, with no markup. They review it and accept it.' },
  { title: 'Home Shopping',
    desc: 'Once a SmartEstimate is selected, the home shopping feature is unlocked. Your {CLIENT} can browse a live MLS feed directly inside their Doorly portal and shop for homes within their approved range. Doorly coordinates showing requests and offer requests through the platform.',
    realtor: { title: 'Home Selection — Your Work',
      desc: 'This is the part you are engaged to do. Identify properties that fit your buyer, arrange and run the showings, and advise them on choosing a specific home. Doorly does not do the house-hunting and does not coordinate showings for your buyers — you do. Home selection has to be completed before Doorly can make an offer.' } },
  { title: 'Offer Requested',
    desc: "When your {CLIENT} has found the home they want, they submit an offer request through their portal. Doorly's internal real estate team takes it from there and coordinates the offer submission on their behalf.",
    realtor: { title: 'Your Buyer Requests It',
      desc: "Once your buyer has chosen a home, point them to Homes in their own Doorly account — they request the offer on that listing themselves. There is no screen on your side for reporting a property. Doorly's internal real estate team takes it from there and writes the offer — you do not write it and you do not negotiate on Doorly's behalf. Your home-selection work concludes when Doorly makes the offer." } },
  { title: 'Under Contract',
    desc: 'Your {CLIENT} goes under contract on a property. The executed purchase agreement is submitted and earnest money is sent.' },
  { title: 'Option Period',
    desc: 'Doorly opens two simultaneous title files to facilitate the double close. The Loan Estimate is generated and e-signed by your {CLIENT} directly in their portal. Inspection and appraisal are both ordered by your {CLIENT} through the platform during this period.' },
  { title: 'Closing',
    desc: 'The Closing Disclosure is generated and e-signed by your {CLIENT}. The closing date is confirmed and both title files are prepared for simultaneous closing.' },
  { title: 'Double Close',
    desc: 'The A-B transaction closes first — the seller transfers the property to Doorly. The B-C transaction closes on the same day — Doorly transfers the property to your {CLIENT}. Your {CLIENT} receives their keys.' },
  { title: 'Post-Closing',
    desc: 'The loan is funded. You receive confirmation that your {CLIENT} has successfully closed. Your {CLIENT} is now a homeowner.' },
];

// ⚠️ TAKES partnerType SO THE THREE REALTOR-DIVERGENT STEPS RESOLVE CORRECTLY.
// An absent type falls through to the base copy — the same fail-soft as the
// status pill, and correct for loan officers and homebuilders whose buyers
// really do self-serve.
function DoorlyProcessModal({ onClose, partnerType }) {
  // ⚠️ SOURCED FROM knowledge/docs/partner-process.md via GET /doorlypro/process,
  // the same document Eva answers partner questions from. The 12-step array
  // below is now ONLY an offline fallback — two hand-written descriptions of one
  // process is the drift this replaces, and the doc is the copy that gets fixed.
  const [doc, setDoc] = useState(null);
  const [docState, setDocState] = useState('loading');
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const token = localStorage.getItem('doorlypro_token');
        const r = await axios.get(`${API_URL}/api/doorlypro/process`, { headers: { Authorization: `Bearer ${token}` } });
        if (!live) return;
        const secs = r.data?.sections || [];
        // ⚠️ An empty parse is a FAILURE, not an empty process. Falling back
        // beats rendering a blank modal.
        if (!secs.length) { setDocState('fallback'); return; }
        setDoc(secs); setDocState('ok');
      } catch (_) { if (live) setDocState('fallback'); }
    })();
    return () => { live = false; };
  }, []);

  // Lock body scroll while open; restore on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      {/* Backdrop — click-to-dismiss */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(17, 24, 39, 0.6)' }} />

      {/* Panel */}
      <div style={{
        position: 'relative', zIndex: 10001,
        background: '#fff', borderRadius: 16,
        width: '100%', maxWidth: 640, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ background: '#3a6ad9', padding: '22px 28px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: -0.2 }}>The Doorly Process</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.75)', letterSpacing: 0.3, textTransform: 'uppercase' }}>
              {docState === 'ok'
                ? 'From submission to closing'
                : `From submission to keys in ${DOORLY_PROCESS_STEPS.length} steps`}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Steps timeline (scrollable) */}
        <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
          {docState === 'loading' && (
            <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>
          )}
          {docState === 'ok' && doc.map((sec, i) => (
            <div key={i} style={{ paddingBottom: 22 }}>
              {/* The lead paragraph has no heading — rendering an empty <h3>
                  left a gap above the first line. */}
              {sec.title && (
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#111827', letterSpacing: -0.1 }}>{sec.title}</h3>
              )}
              {/* ⚠️ These are published help-centre articles, so they carry real
                  markdown — ## subheadings, - bullets, **bold**, | tables.
                  Rendering them as plain lines printed "## Yours" at partners. */}
              {sec.body.split('\n').filter((l) => l.trim()).map((line, j) => {
                const t = line.trim();
                if (t.startsWith('|')) return null;   // the fee table needs its own layout
                if (/^#{2,}\s/.test(t)) return (
                  <div key={j} style={{ margin: '12px 0 2px', fontSize: 12.5, fontWeight: 700, color: '#374151' }}>
                    {t.replace(/^#+\s*/, '')}
                  </div>
                );
                const bullet = /^[-*]\s/.test(t);
                const parts = t.replace(/^[-*]\s*/, '').split(/\*\*(.+?)\*\*/g);
                return (
                  <p key={j} style={{ margin: '5px 0 0', fontSize: 13, color: '#4b5563', lineHeight: 1.65, paddingLeft: bullet ? 14 : 0, textIndent: bullet ? -8 : 0 }}>
                    {bullet ? '• ' : ''}
                    {parts.map((x, k) => (k % 2 ? <strong key={k} style={{ color: '#111827' }}>{x}</strong> : x))}
                  </p>
                );
              })}

            </div>
          ))}
          {docState === 'fallback' && DOORLY_PROCESS_STEPS.map((base, idx) => {
            // Resolve the per-type override. Only title and desc may differ —
            // the sequence is one sequence for everybody.
            // ── 🚨 THE NOUN IS A TOKEN, NOT A FORKED LIST ──────────────────
            //
            // Twelve of these steps said "buyer" in the DEFAULT body — and the
            // default is what a LOAN OFFICER sees, since `realtor` is the
            // override. Adding a realtor override to all twelve to fix the noun
            // would be the second list this file's own header forbids: "a
            // forked twelve-step array would drift on the nine steps that are
            // identical, and the nine identical ones are the ones nobody would
            // think to check."
            //
            // 🔑 SO ONLY THE WORD VARIES. The override stays for the steps whose
            // MEANING differs; everything else is one sentence with one noun
            // resolved at render.
            const CLIENT = partnerType === 'realtor' ? 'buyer' : 'borrower';
            const CLIENT_TITLE = partnerType === 'realtor' ? 'Buyer' : 'Borrower';
            const fill = (t) => String(t || '')
              .replace(/\{CLIENT_TITLE\}/g, CLIENT_TITLE).replace(/\{CLIENT\}/g, CLIENT);
            const resolved = (partnerType && base[partnerType])
              ? { ...base, ...base[partnerType] } : base;
            const step = { ...resolved, title: fill(resolved.title), desc: fill(resolved.desc) };
            const isLast = idx === DOORLY_PROCESS_STEPS.length - 1;
            const circleBg = isLast ? '#acf44e' : '#3a6ad9';
            const circleColor = isLast ? '#111827' : '#fff';
            return (
              <div key={idx} style={{ position: 'relative', display: 'flex', gap: 16, paddingBottom: isLast ? 4 : 18 }}>
                {/* Timeline rail (stops at the last circle) */}
                {!isLast && (
                  <div style={{ position: 'absolute', left: 15, top: 32, bottom: 0, width: 2, background: '#e5e7eb' }} />
                )}

                {/* Step circle */}
                <div style={{
                  position: 'relative', zIndex: 1,
                  width: 32, height: 32, flexShrink: 0,
                  borderRadius: '50%', background: circleBg, color: circleColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800,
                  boxShadow: isLast ? '0 0 0 4px rgba(172, 244, 78, 0.18)' : '0 0 0 4px rgba(58, 106, 217, 0.12)',
                }}>
                  {idx + 1}
                </div>

                {/* Step body */}
                <div style={{ flex: 1, paddingTop: 4 }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', letterSpacing: -0.1 }}>
                    {step.title}
                    {isLast && (
                      <span style={{ marginLeft: 8, display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#acf44e', verticalAlign: 'middle' }} />
                    )}
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 28px', borderTop: '1px solid #f3f4f6', background: '#f9fafb', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ padding: '8px 20px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
